import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowUpRight,
	Check,
	Facebook,
	Globe,
	Image as ImageIcon,
	Instagram,
	Loader2,
	Mail,
	MessageCircle,
	Twitter,
	Upload
} from 'lucide-react';

import shopApi, { saveFileToS3 } from '@services/shop';

import { Button } from '@/components/ui/button';
import { SoftCard } from '@/components/ui/soft-card';
import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useInvalidateLocalPotential } from '@/hooks/useInvalidateLocalPotential';
import { SettingsHeader } from './components/SettingsHeader';

const FAVICON_MIME_TYPES = ['image/png', 'image/jpeg'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

const SOCIAL_FIELDS = [
	{ key: 'website', label: 'Website', placeholder: 'https://yourshop.co.uk', icon: Globe },
	{ key: 'contactEmail', label: 'Contact email', placeholder: 'hello@yourshop.co.uk', icon: Mail },
	{ key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourshop', icon: Facebook },
	{ key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourshop', icon: Instagram },
	{ key: 'twitter', label: 'X / Twitter', placeholder: 'https://x.com/yourshop', icon: Twitter }
];

function StorefrontInput({ field, value, onSave, disabled }) {
	const [local, setLocal] = useState(value ?? '');
	const Icon = field.icon;
	const filled = !!local?.trim();

	const handleBlur = () => {
		const next = local.trim();
		if (next === (value ?? '')) return;
		onSave(field.key, next || null);
	};

	return (
		<div className="space-y-1.5">
			<label htmlFor={`social-${field.key}`} className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
				<Icon className="h-3.5 w-3.5 text-muted-foreground" /> {field.label}
			</label>
			<div className="relative">
				<input
					id={`social-${field.key}`}
					type="text"
					value={local}
					onChange={(event) => setLocal(event.target.value)}
					onBlur={handleBlur}
					placeholder={field.placeholder}
					disabled={disabled}
					className="w-full rounded-xl border border-border bg-card px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground transition focus:border-action focus:outline-none focus:ring-2 focus:ring-action/30 disabled:cursor-not-allowed disabled:opacity-60"
				/>
				{filled ? <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-success-soft-foreground" /> : null}
			</div>
		</div>
	);
}

function PolicyCard({ id, title, helperText, value, onSave, disabled }) {
	const [local, setLocal] = useState(value ?? '');

	const handleBlur = () => {
		const next = local;
		if (next === (value ?? '')) return;
		onSave(next);
	};

	return (
		<SoftCard className="space-y-2 !p-5 md:!p-6">
			<label htmlFor={id} className="text-sm font-bold text-foreground">
				{title}
			</label>
			<p className="text-xs text-muted-foreground">{helperText}</p>
			<textarea
				id={id}
				value={local}
				onChange={(event) => setLocal(event.target.value)}
				onBlur={handleBlur}
				rows={5}
				disabled={disabled}
				className="w-full resize-y rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground transition focus:border-action focus:outline-none focus:ring-2 focus:ring-action/30 disabled:cursor-not-allowed disabled:opacity-60"
			/>
		</SoftCard>
	);
}

function ImageTile({ url, initial, alt, size, rounded, icon: FallbackIcon }) {
	// A stored URL can point at storage we no longer control (NEA-6276), so treat a
	// load failure the same as having no image rather than showing a broken icon.
	const [failed, setFailed] = useState(false);
	useEffect(() => setFailed(false), [url]);

	return (
		<div className={`flex ${size} shrink-0 items-center justify-center overflow-hidden border border-border bg-muted ${rounded}`}>
			{url && !failed ? (
				<img src={url} alt={alt} className="h-full w-full object-cover" onError={() => setFailed(true)} />
			) : initial ? (
				<span className="text-2xl font-bold text-muted-foreground">{initial}</span>
			) : (
				<FallbackIcon className="h-6 w-6 text-muted-foreground" />
			)}
		</div>
	);
}

function ImageUploadRow({
	title,
	description,
	fieldKey,
	value,
	initial,
	acceptedTypes,
	acceptAttribute,
	tileSize,
	tileRounded,
	fallbackIcon,
	shop,
	isPending,
	persist,
	toast
}) {
	const [uploading, setUploading] = useState(false);
	const inputRef = useRef(null);
	const disabled = isPending(fieldKey) || uploading;

	const handleFile = async (file) => {
		if (acceptedTypes && !acceptedTypes.includes(file.type)) {
			toast({
				title: 'Wrong file type',
				description: `Please choose ${acceptedTypes.map((type) => type.split('/')[1].toUpperCase()).join(' or ')}.`,
				variant: 'destructive'
			});
			return;
		}
		if (file.size > MAX_UPLOAD_BYTES) {
			toast({ title: 'File too large', description: 'Maximum size is 5 MB.', variant: 'destructive' });
			return;
		}
		setUploading(true);
		try {
			const url = await saveFileToS3(shop.id, file, { field: fieldKey });
			await persist({ [fieldKey]: url }, fieldKey);
		} catch (err) {
			toast({ title: 'Upload failed', description: err?.message ?? 'Try again', variant: 'destructive' });
		} finally {
			setUploading(false);
		}
	};

	const handleChange = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		// Reset so choosing the same file twice re-fires onChange.
		event.target.value = '';
		await handleFile(file);
	};

	const handleRemove = () => persist({ [fieldKey]: null }, fieldKey);

	return (
		<div className="flex items-start gap-4">
			<ImageTile url={value} initial={initial} alt={title} size={tileSize} rounded={tileRounded} icon={fallbackIcon} />
			<div className="flex-1 space-y-1">
				<h3 className="text-sm font-bold text-foreground">{title}</h3>
				<p className="text-xs text-muted-foreground">{description}</p>
				<div className="flex flex-wrap items-center gap-2 pt-2">
					<Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>
						{uploading ? <Loader2 className="animate-spin" /> : <Upload />}
						{uploading ? 'Uploading…' : value ? 'Replace' : 'Upload'}
					</Button>
					{value ? (
						<Button type="button" variant="ghost" size="sm" onClick={handleRemove} disabled={disabled}>
							Remove
						</Button>
					) : null}
					<input ref={inputRef} type="file" accept={acceptAttribute} className="hidden" onChange={handleChange} />
				</div>
			</div>
		</div>
	);
}

function StorefrontInfoSkeleton() {
	return (
		<div className="space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-12 w-72" />
			<Skeleton className="h-64" />
			<Skeleton className="h-48" />
		</div>
	);
}

export function StorefrontInfo() {
	const { shop, mutate } = useShop();
	const { shopPath } = useShopPath();
	const { toast } = useToast();
	const invalidateScore = useInvalidateLocalPotential();
	const [pending, setPending] = useState(new Set());

	const filledSocials = useMemo(() => {
		if (!shop) return 0;
		return SOCIAL_FIELDS.filter((field) => shop[field.key]?.trim?.()).length;
	}, [shop]);

	if (!shop) return <StorefrontInfoSkeleton />;

	const isPending = (key) => pending.has(key);

	const persist = async (patch, fieldKey) => {
		setPending((curr) => new Set(curr).add(fieldKey));
		try {
			await shopApi.updateMyShop(shop.id, patch);
			// Website / socials feed the score's Links bucket — revalidate the gauge.
			await Promise.all([mutate?.(), invalidateScore()]);
		} catch (err) {
			toast({ title: 'Could not save', description: err?.message ?? 'Try again', variant: 'destructive' });
		} finally {
			setPending((curr) => {
				const next = new Set(curr);
				next.delete(fieldKey);
				return next;
			});
		}
	};

	const handleSocialSave = (key, value) => persist({ [key]: value }, key);
	const handlePolicySave = (key) => (value) => persist({ [key]: value }, key);

	const handleMessengerToggle = async (event) => {
		const checked = event.target.checked;
		if (checked) {
			const handle = shop.facebook?.split('/').filter(Boolean).pop();
			if (!handle) {
				toast({
					title: 'Add a Facebook link first',
					description: 'We use the page handle to generate the m.me link.',
					variant: 'warning'
				});
				return;
			}
			await persist({ facebookMessenger: `https://m.me/${handle}` }, 'facebookMessenger');
		} else {
			await persist({ facebookMessenger: null }, 'facebookMessenger');
		}
	};

	const storefrontUrl = shop.slug ? `https://${shop.slug}.near.st` : null;

	return (
		<div className="max-w-4xl space-y-8">
			<SettingsHeader
				eyebrow="Local Storefront settings"
				title="Local Storefront info"
				description="Manage the details shoppers see on your Local Storefront — the public page Unify gives every product link, social bio, and search result."
				rightSlot={
					storefrontUrl ? (
						<a
							href={storefrontUrl}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1.5 text-sm font-semibold text-action hover:underline"
						>
							View your Local Storefront <ArrowUpRight className="h-3.5 w-3.5" />
						</a>
					) : (
						<Link
							to={shopPath('/channels/storefront')}
							className="inline-flex items-center gap-1.5 text-sm font-semibold text-action hover:underline"
						>
							Local Storefront channel <ArrowUpRight className="h-3.5 w-3.5" />
						</Link>
					)
				}
			/>

			<section>
				<SectionHeader
					eyebrow="Branding"
					title="Logo & favicon"
					sub="A recognisable logo helps shoppers trust your Local Storefront at a glance. The favicon appears in browser tabs and shortcut icons."
				/>
				<SoftCard className="space-y-6 !p-5 md:!p-6">
					<ImageUploadRow
						title="Storefront logo"
						description="Shown at the top of your Local Storefront and in social share previews. Square works best."
						fieldKey="profileImageUrl"
						value={shop.profileImageUrl}
						initial={shop.name ? shop.name.charAt(0).toUpperCase() : '?'}
						acceptAttribute="image/*"
						tileSize="h-20 w-20"
						tileRounded="rounded-2xl"
						fallbackIcon={ImageIcon}
						shop={shop}
						isPending={isPending}
						persist={persist}
						toast={toast}
					/>
					<ImageUploadRow
						title="Favicon"
						description="Shown in browser tabs and shortcut icons. PNG or JPEG only, ideally 64×64 or larger."
						fieldKey="iconImageUrl"
						value={shop.iconImageUrl}
						acceptedTypes={FAVICON_MIME_TYPES}
						acceptAttribute="image/png,image/jpeg"
						tileSize="h-14 w-14"
						tileRounded="rounded-lg"
						fallbackIcon={ImageIcon}
						shop={shop}
						isPending={isPending}
						persist={persist}
						toast={toast}
					/>
				</SoftCard>
			</section>

			<section>
				<SectionHeader
					eyebrow="Most important"
					title="Social media & contact links"
					sub="These appear for shoppers to tap in the header. The more you add, the easier it is for local shoppers to find and trust you."
					rightSlot={
						<span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold tabular-nums text-success-soft-foreground">
							<Check className="h-3 w-3" /> {filledSocials} of {SOCIAL_FIELDS.length} added
						</span>
					}
				/>
				<SoftCard className="space-y-4 !p-5 md:!p-6">
					{SOCIAL_FIELDS.map((field) => (
						<StorefrontInput
							key={`${shop.id}-${field.key}`}
							field={field}
							value={shop[field.key] ?? ''}
							onSave={handleSocialSave}
							disabled={isPending(field.key)}
						/>
					))}

					<label className="flex cursor-pointer items-center gap-2.5 pt-2">
						<input
							type="checkbox"
							checked={!!shop.facebookMessenger}
							onChange={handleMessengerToggle}
							disabled={isPending('facebookMessenger') || !shop.facebook}
							className="h-4 w-4 rounded border-border text-action focus:ring-action/30 disabled:cursor-not-allowed disabled:opacity-60"
						/>
						<span className="inline-flex items-center gap-1.5 text-sm text-foreground">
							<MessageCircle className="h-3.5 w-3.5 text-muted-foreground" />
							Enable Facebook Messenger chat on your Local Storefront
						</span>
					</label>
				</SoftCard>
			</section>

			<section>
				<SectionHeader
					eyebrow="Shopper policies"
					title="Return & delivery policies"
					sub="Shown on every product page. Clear policies reduce hesitation and support requests."
				/>
				<div className="space-y-5">
					<PolicyCard
						key={`${shop.id}-return`}
						id="return-policy"
						title="Return & refund policy"
						helperText="Linked to your Local Storefront and product pages."
						value={shop.returnAndRefundPolicy ?? ''}
						onSave={handlePolicySave('returnAndRefundPolicy')}
						disabled={isPending('returnAndRefundPolicy')}
					/>
					<PolicyCard
						key={`${shop.id}-delivery`}
						id="delivery-policy"
						title="Delivery & collection policy"
						helperText="Tell shoppers how click & collect or delivery works at your shop."
						value={shop.deliveryPolicy ?? ''}
						onSave={handlePolicySave('deliveryPolicy')}
						disabled={isPending('deliveryPolicy')}
					/>
				</div>
			</section>
		</div>
	);
}
